<template>
    <div>
        <div class="grid grid-cols-12 gap-6">
            <div class="hidden md:col-span-1 md:block" />
            <div class="col-span-12 text-left md:col-span-10">
                <h2>Vývoj označení ulic v terénu</h2>
                <div class="mt-5 mb-5">
                    <img
                        src="../../public/img/articles/vyvoj_oznaceni_ulic_v_terenu_1.webp"
                        alt=""
                        class="mb-1 aspect-auto w-full object-cover"
                    />
                    <span class="source"
                        >Nejstarší dochované označení na stěně Svatojiřské
                        baziliky na Pražském Hradě. / Zdroj: Marek
                        Lašťovka</span
                    >
                </div>
                <p class="mb-5 font-bold">
                    Jak se v Praze měnily uliční tabulky? Od malovaných nápisů
                    na zdi přes smaltované cedule až po moderní design –
                    označování pražských ulic má za sebou dlouhý vývoj. Tento
                    článek vás provede základními proměnami uliční navigace od
                    domovních znamení až po dnešní podoby tabulek, které mnohdy
                    skrývají zajímavé příběhy.
                </p>

                <div class="mt-5 mb-5">
                    <img
                        src="../../public/img/articles/vyvoj_oznaceni_ulic_v_terenu_2.webp"
                        alt=""
                        class="mb-1 aspect-auto w-full object-cover"
                    />
                    <span class="source"
                        >Domovní znamení na domě U černého slona na Malé Straně.
                        / Zdroj: Marek Lašťovka</span
                    >
                </div>

                <p class="font-bold">Domovní znamení</p>
                <p class="mb-5">
                    Nejstarší orientační pomůckou ve městě k vyhledání nejen
                    konkrétního domu, ale následně podle něj také přilehlé
                    ulice, byla domovní znamení, která byla většinou snadno
                    zapamatovatelná a mnohá z nich jsou na domech zachována
                    dodnes.
                </p>

                <div class="mt-5 mb-5">
                    <img
                        src="../../public/img/articles/vyvoj_oznaceni_ulic_v_terenu_3.webp"
                        alt=""
                        class="mb-1 aspect-auto w-full object-cover"
                    />
                    <span class="source"
                        >Původní označení Jánského vršku pocházející
                        pravděpodobně z doby okolo poloviny 19. století. /
                        Zdroj: Marek Lašťovka</span
                    >
                </div>

                <p class="font-bold">Nápisy na stěnách domů</p>
                <p class="mb-5">
                    Označení uličních názvů v podobě černých nápisů na stěnách
                    pražských domů se objevila po jejich úředním zavedení koncem
                    18. století. Nápisy byly dvojjazyčné, česky a německy, obojí
                    novogotickým písmem. Dodnes jsou dochovány dva – v

                    <span class="italic">Jiřské</span>

                    a v <span class="italic">Harantově</span> ulici (tehdy
                    Dominikánské). Někdy během 19. století pak byly názvy ulic
                    vyznačovány na fasádách domů novým typem černých nápisů.
                    Opět byly dvojjazyčné, český název humanistickým, německý
                    novogotickým písmem. Provázela je tehdy i čísla popisná
                    domů, které v této ulici či její části ležely. Označování se
                    tehdy provádělo „razítkováním“ přes šablonu, jak je patrné z
                    reklamního letáku firmy Michael Winkler z Vídně, která se i
                    v Praze podílela minimálně na jejich renovaci.
                </p>

                <div class="mt-5 mb-5">
                    <img
                        src="../../public/img/articles/vyvoj_oznaceni_ulic_v_terenu_4.webp"
                        alt=""
                        class="mb-1 aspect-auto w-full object-cover"
                    />
                    <span class="source"
                        >Torzo litinové tabulky pravděpodobně Poříčského sadu
                        (nápis ve tvaru Sad Poříčský), později sadů Jana Švermy.
                        Tento typ tabulek byl používán na přelomu 19. a 20.
                        století. / Zdroj: Muzeum hlavního města Prahy, sign. H
                        339 984.</span
                    >
                </div>

                <p class="font-bold">Uliční tabulky</p>
                <p class="mb-5">
                    Od poloviny 19. století se začaly prosazovat kovové tabulky.
                    Po přejmenování Václavského a Karlova náměstí a Karlovy
                    ulice předkládal Stavební úřad již v roce 1849 návrh a
                    rozpočet na 21 tabulek ze zinkového plechu. Od roku 1868
                    měly být užívány kovové tabulky jednotně. Bohužel není z
                    pramenů úplně jasné, kdy k naplnění jednotného označování
                    ulic opravdu došlo (zdá se, že tabulky a nápisy na zdech
                    byly po delší dobu užívány paralelně). Nejstarší tabulky
                    byly ploché, obsahovaly pouze název ulice, který z tabulky
                    plasticky vystupoval, stejně jako dodnes užívané ozdobné
                    rámování. Názvy ulic byly původně česko-německé, od roku
                    1894 měly být výhradně česky. V témže roce byla také
                    kodifikována podoba tabulek: v barevné kombinaci bílé písmo
                    v červeném poli s modrým lemováním. Tabulky byly mnohde
                    umisťovány přímo přes starší nápisy, vzhledem k jejich
                    velikosti je však zakrýt nemohly.
                </p>

                <div class="mt-5 mb-5">
                    <img
                        src="../../public/img/articles/vyvoj_oznaceni_ulic_v_terenu_5.webp"
                        alt=""
                        class="mb-1 aspect-auto w-full object-cover"
                    />
                    <span class="source"
                        >Dochovaná tabulka názvu ulice Valdenských (dnešní
                        Kroftova) z doby okupace. / Zdroj: Muzeum hlavního města
                        Prahy, inv. č. H 058 012.</span
                    >
                </div>

                <p class="mb-5">
                    Kromě názvu ulice se na tabulkách v blíže neurčené době
                    objevilo římskými číslicemi vyvedené číslo pražské čtvrti.
                    Patrně od roku 1922 se vedle římského čísla městské části
                    začalo objevovat i jméno katastrálního území. Pravděpodobně
                    až tehdy také vznikly vypouklé tabulky, jak je známe dnes.
                    Do dnešní doby se varianty těchto označení měnily podle
                    správních reforem proměňujících čísla a počet pražských
                    městských částí. Když se v roce 1960 změnilo správní členění
                    Prahy na 10 obvodů, stalo se katastrální území spolu s
                    číslem tohoto obvodu nedílnou součástí uliční tabulky, a i
                    přes další vývoj a narůstání počtu pražských městských částí
                    se tento způsob označení udržel doposud.
                </p>

                <div class="mt-5 mb-5">
                    <img
                        src="../../public/img/articles/vyvoj_oznaceni_ulic_v_terenu_6.webp"
                        alt=""
                        class="mb-1 aspect-auto w-full object-cover"
                    />
                    <span class="source"
                        >Tabulka na Roztylském náměstí z období první
                        Československé republiky respektuje číslování čtvrtí
                        římskými číslicemi z let 1922–1949. / Zdroj: Marek
                        Lašťovka</span
                    >
                </div>

                <p class="font-bold">Doba okupace</p>
                <p class="mb-5">
                    Přerušení vývoje tohoto označení představovala pouze doba
                    mezi lety 1940–1945, kdy bylo okupační správou nařízeno v
                    Praze ulice opět označovat dvojjazyčně česky a německy, v
                    němčině se vrátilo gotické písmo, tabulky byly vyvedeny
                    bílým písmem a rámováním na červeném poli. Obsahovaly i
                    katastrální území a číslo čtvrti. Tyto tabulky byly mnohde
                    živelně odstraněny během Pražského povstání. Poté se
                    označování ulic vrátilo k předválečnému stavu.
                </p>

                <div class="mt-5 mb-5">
                    <img
                        src="../../public/img/articles/vyvoj_oznaceni_ulic_v_terenu_7.webp"
                        alt=""
                        class="mb-1 aspect-auto w-full object-cover"
                    />
                    <span class="source"
                        >V okrajových částech Prahy místy přetrvávají tabulky
                        ulic z období před připojením obce k Praze, zde
                        nebušická ulice U Pohádky. / Zdroj: Marek Lašťovka</span
                    >
                </div>

                <p class="font-bold">Řád pro označování 1960</p>
                <p class="mb-5">
                    V souvislosti se správní reformou schválila rada Ústředního
                    národního výboru v roce 1960

                    <span class="italic">
                        Řád pro jednotné označování veřejných prostranství a
                        číslování domů
                    </span>

                    , který stanovil způsob i ochranu označování. Podle něj byly
                    uliční tabule s názvem veřejného prostranství plechové,
                    červeně smaltované s modrou obrubou, obdélníkové o délce 80
                    cm a výšce 40 cm. Název byl uveden bílými písmeny vysokými
                    devět cm, označení katastrálního území a městského obvodu
                    pak písmeny vysokými pět cm. Tento řád byl ovšem zrušen
                    usnesením zastupitelstva hl. m. Prahy v roce 1999.
                </p>

                <div class="mt-5 mb-5">
                    <img
                        src="../../public/img/articles/vyvoj_oznaceni_ulic_v_terenu_8.webp"
                        alt=""
                        class="mb-1 aspect-auto w-full object-cover"
                    />
                    <span class="source"
                        >Současná podoba uličních tabulek. / Zdroj: Marek
                        Lašťovka</span
                    >
                </div>

                <p class="font-bold">Jednotný vzhled 2012</p>
                <p class="mb-5">
                    V roce 2012 byl pak usnesením rady hlavního města Prahy
                    schválen jednotný vzhled uličních tabulí na území hl. m.
                    Prahy podle grafického manuálu, který vytvořili studenti
                    Vysoké školy uměleckoprůmyslové v Praze Filip Kraus, Jan
                    Kříbek, Jan Novák a Vojtěch Říha pod vedením Mgr. Filipa
                    Blažka. Grafický manuál respektuje tradiční provedení tabule
                    a zavádí novou speciální sadu písma na tabule „Smalt“. Město
                    Praha zamýšlelo také grafickou podobu tabulky registrovat
                    jako průmyslový vzor a zabránit tím jejímu zneužívání,
                    protože se na mnohých místech objevují tabulky pro
                    prostranství či domy, které nejsou úředně pojmenovány, a
                    mohou tak působit zmatek. Z mnoha důvodů však celkovou
                    podobu cedulí chránit nelze, lze ochraňovat pouze výše
                    uvedené písmo. Pro hlavní město Prahu dodávají nyní tabule a
                    tabulky dvě smaltovny, a to Smaltovna Gaen v
                    Praze-Hlubočepích a smaltovna SMALT, s. r. o., v
                    Želechovicích.
                </p>
                <p class="mb-10 sm:mb-25">
                    Dnes můžeme po celé Praze nalézt označení ulic z
                    nejrůznějších dob, protože v žádné době nebyly tabulky vždy
                    vyměněny všechny. Někde jsou dodnes umístěny tabulky z doby
                    před připojením ulice k Praze, a mají tak proto jinou
                    podobu. V poslední době se také objevují levnější plastové
                    cedule.
                </p>

                <!-- next chapters -->
                <div class="mb-10 flex justify-between border-t-1 pt-5">
                    <p class="mb-5 w-1/2 border-r-1 font-bold">
                        <nuxt-link
                            to="/clanky/pocty-ulic"
                            class="flex justify-start underline"
                        >
                            <img
                                src="../../public/img/left-arrow.png"
                                alt=""
                                class="order-1 mr-2 inline h-5 w-5"
                            />
                            <span class="order-2 mr-2">Počty ulic</span>
                        </nuxt-link>
                    </p>
                    <p class="mb-5 w-1/2 text-right font-bold">
                        <nuxt-link
                            to="/clanky/pravopis-a-jazykova-spravnost-nazvu"
                            class="flex justify-end underline"
                        >
                            <span class="order-1 ml-2"
                                >Pravopis a jazyková správnost názvů</span
                            >
                            <img
                                src="../../public/img/right-arrow.png"
                                alt=""
                                class="order-2 ml-2 inline h-5 w-5"
                            />
                        </nuxt-link>
                    </p>
                </div>
            </div>
            <div class="hidden md:col-span-1 md:block" />
        </div>

        <AppFooter />
    </div>
</template>

<script setup lang="ts"></script>

<style scoped></style>
